import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, Sparkles, X } from "lucide-react";
import "./AiBoardTour.css";

/* =============================================================
   The AI Board's walkthrough -- a first-run guide, the way a game
   shows its controls: the page dims, a lit cut-out glides to one
   control at a time, and a card beside it says what that control
   does. Back / Next / Skip, or the arrow keys and Esc.

   It opens by itself the first time someone visits the board (kept in
   this browser), and any time from the board's Guide button. A step
   whose control is not on the page -- the microphone in a browser
   without speech, the suggestions once a conversation has started --
   is passed over. Steps with no control (the welcome, the last word)
   sit in the middle of the screen.
   ============================================================= */

export const TOUR_SEEN_KEY = "aiBoard.tourSeen";

const STEPS = [
  {
    title: "Welcome to the AI Board",
    body: "Ask about the LMS data in plain words -- batches, students, fees, attendance -- and get the answer as figures, tables and charts. Here is what everything does.",
  },
  {
    target: ".aib-page .chat-input",
    title: "Ask anything",
    body: 'Type a question, like "ongoing batches per course" or "fees collected this month". Press Enter or the send button to ask.',
  },
  {
    target: ".aib-page .aib-colls",
    title: "Where to look",
    body: "The collections the question reads. They are picked for you from your words as you type -- even misspelt -- and you can add or remove any here.",
  },
  {
    target: ".aib-page .aib-model",
    title: "Choose the AI",
    body: "The AI model that writes the query. The default is the strongest; a smaller one answers faster when the limit is near.",
  },
  {
    target: ".aib-page .aib-viz",
    title: "Charts",
    body: "Tick it to get answers drawn as bar, line, pie and donut charts where the data makes one. The table is always one click away.",
  },
  {
    target: ".aib-page .aib-mic",
    title: "Speak instead",
    body: "Press the microphone and say your question -- it is typed into the box for you. Nothing is sent until you press send.",
  },
  {
    target: ".aib-page .chat-send",
    title: "Send",
    body: "Sends the question. The AI writes a plan, the server runs it on the live data, and the rows come back -- the AI never sees the data itself.",
  },
  {
    target: ".aib-page .aib-marquee",
    title: "Not sure what to ask?",
    body: "Try one of these. Point at the strip to pause it, then click a question to ask it straight away.",
  },
  {
    target: ".aib-page .chat-canvas",
    title: "Your answers",
    body: "Answers appear here. Under each one: Listen reads it aloud, Ask again repeats it, Download saves it as CSV or Excel, and Copy puts it on the clipboard. Open \"Query\" to see how it was worked out.",
    place: "inside",
  },
  {
    target: ".aib-page .aib-head .aib-new",
    title: "Start over",
    body: "Clears the conversation for a fresh start. Your collection, model and chart choices are kept.",
  },
  {
    title: "You're all set",
    body: "Ask your first question. You can replay this guide any time from the Guide button at the top.",
  },
];

const GAP = 12; // between the lit control and the card
const PAD = 6; // around the control, inside the light
const EDGE = 12; // the card's least distance from the screen's edge

const visible = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

const AiBoardTour = ({ open, onClose }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState(null);
  const cardRef = useRef(null);

  // The steps that can be shown now: their control is on the page.
  const stepsNow = useCallback(() => STEPS.filter((s) => !s.target || visible(document.querySelector(s.target))), []);
  const [steps, setSteps] = useState(STEPS);

  useEffect(() => {
    if (open) {
      setSteps(stepsNow());
      setIndex(0);
    }
  }, [open, stepsNow]);

  const step = steps[index];
  const last = index === steps.length - 1;

  const finish = useCallback(() => {
    try {
      localStorage.setItem(TOUR_SEEN_KEY, "1");
    } catch (e) {
      // Not kept: the guide may open again next time, nothing worse.
    }
    onClose();
  }, [onClose]);

  const next = useCallback(() => (last ? finish() : setIndex((i) => i + 1)), [last, finish]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  // Where the light and the card go, kept up as the page moves.
  const place = useCallback(() => {
    if (!step) return;
    const el = step.target ? document.querySelector(step.target) : null;
    const card = cardRef.current;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cw = card ? card.offsetWidth : 340;
    const ch = card ? card.offsetHeight : 200;

    if (!el || !visible(el)) {
      setRect(null);
      setCardPos({ top: Math.max(EDGE, (vh - ch) / 2), left: Math.max(EDGE, (vw - cw) / 2) });
      return;
    }
    const r = el.getBoundingClientRect();
    const lit = { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
    setRect(lit);

    let top;
    let left;
    if (step.place === "inside") {
      top = lit.top + Math.min(40, lit.height / 4);
      left = lit.left + (lit.width - cw) / 2;
    } else {
      // Below the control when there is room, else above it.
      const below = lit.top + lit.height + GAP;
      const above = lit.top - GAP - ch;
      top = below + ch <= vh - EDGE || above < EDGE ? below : above;
      left = lit.left + lit.width / 2 - cw / 2;
    }
    setCardPos({
      top: Math.min(Math.max(EDGE, top), vh - ch - EDGE),
      left: Math.min(Math.max(EDGE, left), vw - cw - EDGE),
    });
  }, [step]);

  // Bring the control into view, then place everything around it.
  useLayoutEffect(() => {
    if (!open || !step) return undefined;
    const el = step.target ? document.querySelector(step.target) : null;
    if (el && step.place !== "inside") el.scrollIntoView({ block: "nearest", inline: "nearest" });
    place();
    const frame = requestAnimationFrame(place);
    return () => cancelAnimationFrame(frame);
  }, [open, step, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => place();
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, place]);

  // Keys: arrows to move, Esc to leave.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        back();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, next, back, finish]);

  // The card takes focus on every step, so the keys and a screen reader
  // are always with it.
  useEffect(() => {
    if (open) cardRef.current?.focus();
  }, [open, index]);

  if (!open || !step) return null;

  return createPortal(
    <div className="aib-tour" aria-live="polite">
      {/* The dim, with a lit cut-out around the control (or all dim). */}
      {rect ? (
        <div className="aib-tour-light" style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} />
      ) : (
        <div className="aib-tour-dim" />
      )}

      <div
        ref={cardRef}
        className={`aib-tour-card${rect ? "" : " is-centre"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="aib-tour-title"
        aria-describedby="aib-tour-body"
        tabIndex={-1}
        style={cardPos ? { top: cardPos.top, left: cardPos.left } : { visibility: "hidden" }}
      >
        <div className="aib-tour-bar" aria-hidden="true">
          <span style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
        </div>
        <header className="aib-tour-head">
          <span className="aib-tour-step">
            <Sparkles size={13} aria-hidden="true" /> {index + 1} of {steps.length}
          </span>
          <button type="button" className="aib-tour-x" onClick={finish} aria-label="Close the guide">
            <X size={16} aria-hidden="true" />
          </button>
        </header>
        <h3 id="aib-tour-title" className="aib-tour-title">
          {step.title}
        </h3>
        <p id="aib-tour-body" className="aib-tour-body">
          {step.body}
        </p>
        <footer className="aib-tour-foot">
          {!last && (
            <button type="button" className="aib-tour-skip" onClick={finish}>
              Skip
            </button>
          )}
          <span className="aib-tour-grow" />
          {index > 0 && (
            <button type="button" className="pg-btn aib-tour-btn" onClick={back}>
              <ArrowLeft size={14} aria-hidden="true" /> Back
            </button>
          )}
          <button type="button" className="pg-btn pg-btn--main aib-tour-btn" onClick={next}>
            {last ? "Start asking" : index === 0 ? "Show me" : "Next"}
            {!last && <ArrowRight size={14} aria-hidden="true" />}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default AiBoardTour;
